import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Grid, 
  TextField, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Select,
  Autocomplete,
  InputAdornment,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  FormControlLabel,
  Switch,
  useMediaQuery,
  useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import { getOltIps, getActiveAlerts } from '../../services/api';
import axios from 'axios';

const SIGNAL_CONDITIONS = [
  { value: '', label: 'Все' },
  { value: 'no_signal', label: 'Нет сигнала' },
  { value: 'has_signal', label: 'Есть сигнал' },
  { value: 'out_of_range', label: 'Критический уровень' }
];

// Окна времени для фильтра «недавно упали».
const DROP_WINDOWS = [
  { value: 5, label: '5 мин' },
  { value: 15, label: '15 мин' },
  { value: 30, label: '30 мин' },
  { value: 60, label: '60 мин' },
];

const FilterPanel = ({ filters, onFilterChange }) => {
  const [oltIps, setOltIps] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [showProblemPorts, setShowProblemPorts] = useState(false);
  const [loading, setLoading] = useState(false);
  // Окно и режим фильтра «недавно упали».
  const [dropWindow, setDropWindow] = useState(15);
  const [showDropouts, setShowDropouts] = useState(false);
  const [severeOnly, setSevereOnly] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchOltIps = async () => {
      try {
        const data = await getOltIps();
        console.log('Received OLT IPs:', data);
        setOltIps(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching OLT IPs:', error);
      }
    };
    fetchOltIps();
  }, []);

  const handleOltIpChange = (event, newValue) => {
    onFilterChange({ ...filters, olt_ip: newValue ? newValue.ip : '' });
  };

  const handleSignalConditionChange = (event) => {
    onFilterChange({ ...filters, signal_condition: event.target.value });
  };

  const handleSearchChange = (event) => {
    onFilterChange({ ...filters, search: event.target.value, description: '' });
  };

  const handleResetFilters = () => {
    setShowProblemPorts(false);
    setShowDropouts(false);
    setSevereOnly(false);
    onFilterChange({
      olt_ip: '',
      description: '',
      search: '',
      signal_condition: '',
      page: 0
    });
  };

  const handleAccordionChange = () => {
    setExpanded(!expanded);
  };

  // Добавлен обработчик для проблемных портов
  const handleFilterChange = (filterName, value) => {
    if (filterName === 'problemPorts') {
      setShowProblemPorts(value);
      // Включение LOS-фильтра выключает dropouts (взаимоисключающие источники).
      if (value) {
        setShowDropouts(false);
        fetchProblemPorts();
      } else {
        onFilterChange(filters);
      }
    }
  };

  // Переключатель «недавно упали»: тянет результат детектора переходов UP→DOWN.
  const handleDropoutsChange = (checked) => {
    setShowDropouts(checked);
    if (checked) {
      setShowProblemPorts(false); // взаимоисключающие источники
      fetchDropouts(severeOnly);
    } else {
      onFilterChange(filters);
    }
  };

  const handleSevereOnlyChange = (checked) => {
    setSevereOnly(checked);
    if (showDropouts) fetchDropouts(checked);
  };

  const handleDropWindowChange = (value) => {
    setDropWindow(value);
    if (showDropouts) fetchDropouts(severeOnly, value);
  };

  const fetchDropouts = async (onlySevere, minutes = dropWindow) => {
    try {
      setLoading(true);
      const data = await getActiveAlerts(minutes, onlySevere);
      onFilterChange({
        type: 'dropouts',
        data,
        severeOnly: onlySevere,
        dropWindow: minutes,
      });
    } catch (error) {
      console.error('Error fetching dropouts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Добавлена функция для получения проблемных портов
  const fetchProblemPorts = async () => {
    try {
      setLoading(true);
      // Используем axios напрямую для запроса проблемных портов
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const response = await axios.get(`${API_URL}/api/signals/problem-ports`);
      
      onFilterChange({
        type: 'problemPorts',
        data: response.data
      });
    } catch (error) {
      console.error('Error fetching problem ports:', error);
    } finally {
      setLoading(false);
    }
  };

  // Мобильная версия фильтров
  if (isMobile) {
    return (
      <Box sx={{ mb: 2 }}>
        <Accordion 
          expanded={expanded} 
          onChange={handleAccordionChange}
          sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="filter-panel-content"
            id="filter-panel-header"
            sx={{ 
              backgroundColor: 'background.paper',
              borderRadius: 1,
              boxShadow: 1
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterListIcon />
              <Typography>Фильтры</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Поиск"
                  value={filters.search || ''}
                  onChange={handleSearchChange}
                  placeholder="Поиск по всем полям..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Autocomplete
                  size="small"
                  options={oltIps}
                  value={oltIps.find(ip => ip.ip === filters.olt_ip) || null}
                  onChange={handleOltIpChange}
                  getOptionLabel={(option) => `${option.ip} - ${option.description}`}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="OLT IP"
                      placeholder="Выберите IP адрес"
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.ip === value?.ip}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Состояние сигнала</InputLabel>
                  <Select
                    value={filters.signal_condition || ''}
                    onChange={handleSignalConditionChange}
                    label="Состояние сигнала"
                  >
                    {SIGNAL_CONDITIONS.map((condition) => (
                      <MenuItem key={condition.value} value={condition.value}>
                        {condition.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Добавлен переключатель для проблемных портов */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showProblemPorts}
                      onChange={(e) => handleFilterChange('problemPorts', e.target.checked)}
                      disabled={loading}
                    />
                  }
                  label="Порты с LOS за последние 72 часа"
                />
              </Grid>

              {/* Фильтр «недавно упали» (мобильная версия) */}
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Окно падений</InputLabel>
                  <Select
                    value={dropWindow}
                    label="Окно падений"
                    onChange={(e) => handleDropWindowChange(Number(e.target.value))}
                    disabled={loading}
                  >
                    {DROP_WINDOWS.map((w) => (
                      <MenuItem key={w.value} value={w.value}>{w.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showDropouts}
                      onChange={(e) => handleDropoutsChange(e.target.checked)}
                      disabled={loading}
                      color="error"
                    />
                  }
                  label="Недавно упали (LOS)"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={severeOnly}
                      onChange={(e) => handleSevereOnlyChange(e.target.checked)}
                      disabled={loading || !showDropouts}
                      color="error"
                    />
                  }
                  label="Только аварии ветки (≥3)"
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<ClearIcon />}
                  onClick={handleResetFilters}
                  fullWidth
                >
                  Сбросить
                </Button>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  }

  // Десктопная версия фильтров
  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={3}>
          <Autocomplete
            size="small"
            options={oltIps}
            value={oltIps.find(ip => ip.ip === filters.olt_ip) || null}
            onChange={handleOltIpChange}
            getOptionLabel={(option) => `${option.ip} - ${option.description}`}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label="OLT IP"
                placeholder="Выберите IP адрес"
              />
            )}
            isOptionEqualToValue={(option, value) => option.ip === value?.ip}
          />
        </Grid>

        <Grid item xs={12} sm={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Состояние сигнала</InputLabel>
            <Select
              value={filters.signal_condition || ''}
              onChange={handleSignalConditionChange}
              label="Состояние сигнала"
            >
              {SIGNAL_CONDITIONS.map((condition) => (
                <MenuItem key={condition.value} value={condition.value}>
                  {condition.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            size="small"
            label="Поиск"
            value={filters.search || ''}
            onChange={handleSearchChange}
            placeholder="Поиск по всем полям..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* Добавлен переключатель для проблемных портов */}
        <Grid item xs={12} sm={2}>
          <FormControlLabel
            control={
              <Switch
                checked={showProblemPorts}
                onChange={(e) => handleFilterChange('problemPorts', e.target.checked)}
                disabled={loading}
              />
            }
            label="Порты с LOS за последние 72 часа"
          />
        </Grid>

        <Grid item xs={12} sm={2}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ClearIcon />}
            onClick={handleResetFilters}
            fullWidth
            size="medium"
          >
            Сбросить
          </Button>
        </Grid>

        {/* Фильтр «недавно упали»: свежие переходы UP→DOWN (LOS) */}
        <Grid item xs={12} sm={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Окно падений</InputLabel>
            <Select
              value={dropWindow}
              label="Окно падений"
              onChange={(e) => handleDropWindowChange(Number(e.target.value))}
              disabled={loading}
            >
              {DROP_WINDOWS.map((w) => (
                <MenuItem key={w.value} value={w.value}>{w.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={3}>
          <FormControlLabel
            control={
              <Switch
                checked={showDropouts}
                onChange={(e) => handleDropoutsChange(e.target.checked)}
                disabled={loading}
                color="error"
              />
            }
            label="Недавно упали (LOS)"
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <FormControlLabel
            control={
              <Switch
                checked={severeOnly}
                onChange={(e) => handleSevereOnlyChange(e.target.checked)}
                disabled={loading || !showDropouts}
                color="error"
              />
            }
            label="Только аварии ветки (≥3)"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default FilterPanel; 